// src/pages/entit/lokal.js
import React, { useMemo, useState, useCallback } from "react";
import { Container, Row, Col, Form, Table } from "react-bootstrap";
import ReactECharts from "echarts-for-react";
import lokalData from "../../assets/data/lokal.json";
import "../../App.css";

// ⭐ export + DATENSTAND
import { DATENSTAND } from "../../config/constants";
import { exportToExcel } from "../../utils/exportExcel";
import { LOKALISATION_COLORS } from "../../config/colors";

// --- formatters ---
const nfInt = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 });
const nfPct1 = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fmtInt = (v) => (v == null ? "–" : nfInt.format(v));
const fmtPct = (v) => (v == null ? "–" : nfPct1.format(v));

// extract ICD code
const codeFromCodnam = (s = "") => (s.match(/^[A-Z]\d+(?:\.\d+)?/)?.[0] ?? s);



// sort helper
const compareICD = (a, b) => {
  const pa = /^([A-Z])(\d{2,3})(?:\.(\d))?/.exec(a) || [];
  const pb = /^([A-Z])(\d{2,3})(?:\.(\d))?/.exec(b) || [];
  const la = pa[1] || "", lb = pb[1] || "";
  const ma = pa[2] ? parseInt(pa[2], 10) : 0;
  const mb = pb[2] ? parseInt(pb[2], 10) : 0;
  const sa = pa[3] ? parseInt(pa[3], 10) : -1;
  const sb = pb[3] ? parseInt(pb[3], 10) : -1;

  if (la !== lb) return la.localeCompare(lb);
  if (ma !== mb) return ma - mb;

  const ua = sa === 9 ? 1 : 0;
  const ub = sb === 9 ? 1 : 0;
  if (ua !== ub) return ua - ub;

  return sa - sb;
};

// ⭐ exclude 3 entities
const EXCLUDED_ENTITS = [
  "Chronische lymphatische Leukämie (CLL/SLL)",
  "Multiples Myelom",
  "Diffuses großzelliges B-Zell-Lymphom (DLBCL)",
];

const Lokal = () => {
  // --- dropdown: Jahr & Entität ---
  const years = useMemo(
    () => [...new Set(lokalData.map((d) => d.diayear))].sort((a, b) => (a > b ? 1 : -1)),
    []
  );

  const entits = useMemo(
    () =>
      [...new Set(lokalData.map((d) => d.entit))].sort((a, b) => a.localeCompare(b)),
    []
  );

const [year, setYear] = useState(
  String(years[years.length - 1] ?? "")
);
  const [entit, setEntit] = useState(entits[0] ?? "");

  const isExcluded = EXCLUDED_ENTITS.includes(entit);

  // --- filtered rows ---
  const filtered = useMemo(
    () => lokalData.filter((d) => String(d.diayear) === String(year) && d.entit === entit),
    [year, entit]
  );

  const maleRows = useMemo(() => filtered.filter((d) => d.SEX === "M"), [filtered]);
  const femaleRows = useMemo(() => filtered.filter((d) => d.SEX === "W"), [filtered]);

  const totalM = useMemo(() => maleRows.reduce((s, r) => s + (Number(r.n) || 0), 0), [maleRows]);
  const totalW = useMemo(() => femaleRows.reduce((s, r) => s + (Number(r.n) || 0), 0), [femaleRows]);
  const totalAll = totalM + totalW;

  // colors per ICD code
const colorMap = useMemo(() => {
  const codes = [
    ...new Set([...maleRows, ...femaleRows].map((r) => codeFromCodnam(r.codnam))),
  ].sort((a, b) => a.localeCompare(b));

  const m = new Map();
  codes.forEach((c, i) => {
    const isDot9 =
      typeof c === "string" &&
      c.endsWith(".9") &&
      c !== "C61.9"; // exception for C61.9

   m.set(c,isDot9 ? "#999999" : LOKALISATION_COLORS[i % LOKALISATION_COLORS.length]);
  });
  return m;
}, [maleRows, femaleRows]);



  const pieDataFor = useCallback(
    (rows) =>
      rows
        .filter((r) => r.pct != null)
        .map((r) => ({
          name: codeFromCodnam(r.codnam),
          value: Number(r.pct),
          codnam: r.codnam,
          n: Number(r.n),
          color: colorMap.get(codeFromCodnam(r.codnam)),
        }))
        .sort((a, b) => b.value - a.value),
    [colorMap]
  );

  const malePie = useMemo(() => pieDataFor(maleRows), [maleRows, colorMap, pieDataFor]);
  const femalePie = useMemo(() => pieDataFor(femaleRows), [femaleRows, colorMap, pieDataFor]);

  const basePieOption = {
    tooltip: {
      trigger: "item",
      backgroundColor: "#000",
      borderWidth: 0,
      textStyle: { color: "#fff", fontSize: 12 },
      confine: true,
      formatter: (p) =>
        `<div style="text-align:left;line-height:1.3">
          <div style="font-weight:600;margin-bottom:4px">${p.data?.codnam ?? p.name}</div>
          <div>Anzahl: ${fmtInt(p.data?.n)}</div>
          <div>Anteil: ${fmtPct(p.value)}%</div>
        </div>`,
    },
    legend: { show: false },
    series: [],
  };

  const menOption = {
    ...basePieOption,
    series: [
      {
        name: "Männer",
        type: "pie",
        radius: ["40%", "70%"],
        center: ["50%", "50%"],
        minShowLabelAngle: 2,
        label: { show: true, formatter: (p) => `${p.name}: ${fmtPct(p.value)}%`, fontSize: 12 },
        labelLine: { show: true, length: 12, length2: 8 },
        data: malePie.map((d) => ({
          ...d,
          // ⭐ white border divider
          itemStyle: { color: d.color, borderWidth: 1, borderColor: "#fff" },
        })),
      },
    ],
  };

  const womenOption = {
    ...basePieOption,
    series: [
      {
        name: "Frauen",
        type: "pie",
        radius: ["40%", "70%"],
        center: ["50%", "50%"],
        minShowLabelAngle: 2,
        label: { show: true, formatter: (p) => `${p.name}: ${fmtPct(p.value)}%`, fontSize: 12 },
        labelLine: { show: true, length: 12, length2: 8 },
        data: femalePie.map((d) => ({
          ...d,
          // ⭐ white border divider
          itemStyle: { color: d.color, borderWidth: 1, borderColor: "#fff" },
        })),
      },
    ],
  };

  // ----- table -----
  const tableRows = useMemo(() => {
    const map = new Map();
    const add = (row, sexKey) => {
      const key = row.codnam;
      if (!map.has(key)) {
        map.set(key, {
          codnam: row.codnam,
          code: codeFromCodnam(row.codnam),
          n_m: null,
          n_w: null,
          pct_m: null,
          pct_w: null,
        });
      }
      const rec = map.get(key);
      if (sexKey === "M") {
        rec.n_m = Number(row.n);
        rec.pct_m = Number(row.pct);
      } else {
        rec.n_w = Number(row.n);
        rec.pct_w = Number(row.pct);
      }
    };
    maleRows.forEach((r) => add(r, "M"));
    femaleRows.forEach((r) => add(r, "W"));

    const arr = [...map.values()].map((r) => {
      const n_g = (r.n_m || 0) + (r.n_w || 0);
      const pct_g = totalAll > 0 ? (n_g / totalAll) * 100 : null;
      return { ...r, n_g, pct_g };
    });

    arr.sort((a, b) => compareICD(a.code, b.code));
    return arr;
  }, [maleRows, femaleRows, totalAll]);

  const footer = useMemo(() => {
    const n_g = totalAll;
    const n_m = totalM;
    const n_w = totalW;
    return {
      n_g,
      n_m,
      n_w,
      pct_g: 100,
      pct_m: totalM > 0 ? 100 : null,
      pct_w: totalW > 0 ? 100 : null,
    };
  }, [totalAll, totalM, totalW]);

  // ⭐ EXPORT HANDLER
  const handleExport = () => {
    if (isExcluded) {
      return;
    }

    const excelData = tableRows.map((r) => ({
      Lokalisation: r.codnam,
      Gesamt: r.n_g ?? "–",
      Männer: r.n_m ?? "–",
      Frauen: r.n_w ?? "–",
      "Gesamt (%)": fmtPct(r.pct_g),
      "Männer (%)": fmtPct(r.pct_m),
      "Frauen (%)": fmtPct(r.pct_w),
    }));

    exportToExcel({
      filename: "lokalisation",
      sheetName: "Lokalisation",
      data: excelData,
      metadata: [
        ["Lokalisation"],
        ["Krebsregister Sachsen-Anhalt https://www.kr-lsa.de"],
        [`Datenstand: ${DATENSTAND}`],
        [`Export am: ${new Date().toLocaleString("de-DE")}`],
      ],
    });
  };

  return (
    <Container className="mt-4">
      <h2 className="section-title">Lokalisation</h2>
      <div className="section-title-underline"></div>

      <div className="aktuelles-text" style={{ marginTop: "0.5rem" }}>
        <p>
          Die Beschreibung der Lokalisation erfolgt nach der International
          Classification of Diseases for Oncology (ICD-O-3). Sie beschreibt den
          Sitz des Primärtumors und verwendet in der Regel dieselben
          Schlüsselnummern wie die ICD-10 Klassifikation.
        </p>
        <p>
          Auf dieser Seite sehen Sie die Häufigkeit der Verteilung für die 15
          häufigsten Entitäten, die Sie über das Auswahlmenü für verschiedene
          Diagnosejahre aufrufen können.
        </p>
      </div>

      {/* ⭐ EXPORT BUTTON */}
      <button
        onClick={handleExport}
        className="top-btn mb-3"
        disabled={isExcluded}
      >
        📥 Daten als Excel exportieren
      </button>

      {/* Dropdowns */}
      <div className="d-flex align-items-center gap-3 mb-4" style={{ maxWidth: 720 }}>
        <Form.Group controlId="entitSelect" className="flex-fill">
          <Form.Label>Krebs-Auswahl:</Form.Label>
          <Form.Select value={entit} onChange={(e) => setEntit(e.target.value)}>
            {entits.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group controlId="yearSelect" className="flex-fill">
          <Form.Label>Jahr:</Form.Label>
          <Form.Select value={year} onChange={(e) => setYear(e.target.value)}>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>

      {isExcluded ? (
        <p className="mt-3" style={{ fontSize: "0.95rem", color: "#555" }}>
          Für diese Entität liegen keine Angaben zur Lokalisation vor.
        </p>
      ) : (
        <>
          {/* Pies */}
          <Row className="mb-4">
            <Col md={6}>
              <h5>Männer</h5>
              <ReactECharts option={menOption} style={{ width: "100%", height: 420 }} />
            </Col>
            <Col md={6}>
              <h5>Frauen</h5>
              <ReactECharts option={womenOption} style={{ width: "100%", height: 420 }} />
            </Col>
          </Row>

          {/* Table */}
          <Table
            responsive
            className="zeitverlauf-table text-center align-middle"
            style={{ maxWidth: "1200px" }}
          >
            <thead>
              <tr>
                <th className="text-start">Lokalisation (ICD-O-3)</th>
                <th colSpan={3}>Anzahl</th>
                <th colSpan={3}>Anteil in %</th>
              </tr>
              <tr>
                <th className="text-start"></th>
                <th>Gesamt</th>
                <th>Männer</th>
                <th>Frauen</th>
                <th>Gesamt</th>
                <th>Männer</th>
                <th>Frauen</th>
              </tr>
            </thead>
 <tbody>
  {tableRows.map((r, i) => {
    const isDot9 =
      typeof r.code === "string" &&
      r.code.endsWith(".9") &&
      r.code !== "C61.9"; // exception for C61.9

    return (
      <tr
        key={`${r.code}-${i}`}
        style={isDot9 ? { color: "#777777" } : undefined}
      >
        <td className="text-start">{r.codnam}</td>
        <td>{fmtInt(r.n_g)}</td>
        <td>{fmtInt(r.n_m)}</td>
        <td>{fmtInt(r.n_w)}</td>
        <td>{fmtPct(r.pct_g)}</td>
        <td>{fmtPct(r.pct_m)}</td>
        <td>{fmtPct(r.pct_w)}</td>
      </tr>
    );
  })}
</tbody>

            <tfoot>
              <tr className="table-secondary">
                <td className="text-start fw-bold">Gesamt</td>
                <td className="fw-bold">{fmtInt(footer.n_g)}</td>
                <td className="fw-bold">{fmtInt(footer.n_m)}</td>
                <td className="fw-bold">{fmtInt(footer.n_w)}</td>
                <td className="fw-bold">{fmtPct(footer.pct_g)}</td>
                <td className="fw-bold">{fmtPct(footer.pct_m)}</td>
                <td className="fw-bold">{fmtPct(footer.pct_w)}</td>
              </tr>
            </tfoot>
          </Table>
        </>
      )}
    </Container>
  );
};

export default Lokal;
